import PropTypes from "prop-types";
import { colors, abbreviateNumber } from "../ReportsHelper";
import NoData from "../NoData";
import ReportChart from "./ReportChart";
import { VictoryBar, VictoryGroup, VictoryLabel } from "victory";
import { ReportChartConsts } from "../ReportTooltips";

export default function TwoMonthComparisonChart({ data }) {
    const labelColor = document.documentElement.getAttribute('data-gdstheme') === 'dark' ? "#ffffff" : "#000000";
    if (!data || data.length === 0) {
        return (<ReportChart {...ReportChartConsts('monthOverMonth')}>
            <NoData />
        </ReportChart>);
    }

    data = data?.slice(0, 2);

    const keys = ["Quotes", "NewBusiness", "Conversion", "BoundPremium"];
    const metricLabels = [
        { text: "Quotes", x: 52, y: 4 },
        { text: "New Business", x: 71, y: 86 },
        { text: "Conversion (Closure)", x: 91, y: 168 },
        { text: "New Business Written Premium", x: 122, y: 250 },
    ];

    return (
        <ReportChart {...ReportChartConsts('monthOverMonth')}>
            <VictoryGroup horizontal offset={20} colorScale={colors} domain={{ y: [0, 185] }} padding={{ bottom: 20, left: 30, right: 20, top: 35 }} style={{ data: { width: 20 } }}>
                {data.map((d, index) => {
                    const metrics = [
                        { key: "BoundPremium", label: "New Business Written Premium", format: (v) => v },
                        { key: "Conversion", label: "Conversion", format: (v) => `${(v * 100).toLocaleString()}%` },
                        { key: "NewBusiness", label: "New Business", format: (v) => v },
                        { key: "Quotes", label: "Quotes", format: (v) => v },
                    ];

                    const maxValues = Object.fromEntries(
                        metrics.map(({ key }) => [key, Math.max(...data.map((item) => item.Data[key]))])
                    );

                    const normalizedData = metrics.map(({ key, label, format }) => ({
                        x: label,
                        y: Math.max((d.Data[key] / (maxValues[key] || 1)) * 100, 0.1),
                        original: format(d.Data[key]),
                    }));

                    return (
                        <VictoryBar
                            key={index}
                            data={normalizedData}
                            labels={({ datum }) => datum.original.toLocaleString()}
                            labelComponent={<VictoryLabel style={{ fontSize: 10, fill: labelColor }} />}
                            style={{
                                data: { fill: colors[index] },
                            }}
                        />
                    );
                })}

                {metricLabels.map((label, index) => (
                    <VictoryLabel
                        key={index}
                        text={label.text}
                        x={label.x}
                        y={label.y}
                        style={{ fontSize: 12 }}
                        textAnchor="middle"
                    />
                ))}

                {keys.map((metric, index) => {
                    let current = data[0].Data[metric] * (metric === "Conversion" ? 100 : 1);
                    let prior = data[1].Data[metric] * (metric === "Conversion" ? 100 : 1);
                    const percentageChanged = prior === 0 ? 0 : (((current - prior) / prior) * 100).toFixed(2);
                    const sign = current >= prior ? "+" : "-";

                    const labelText = metric === "Conversion"
                        ? [`${abbreviateNumber(current)}%`, `Prior: ${abbreviateNumber(prior)}% (${sign}${abbreviateNumber(current - prior)} points)`]
                        : [abbreviateNumber(current), `Prior: ${abbreviateNumber(prior)} (${sign}${percentageChanged}%)`];

                    return (
                        <VictoryLabel
                            key={index}
                            text={labelText}
                            x={350}
                            y={metricLabels[index].y + 30}
                            style={{
                                fontSize: 10,
                                fill: current >= prior ? "green" : "red",
                            }}
                            textAnchor="middle"
                        />
                    );
                })}
            </VictoryGroup>
        </ReportChart>
    );
}

TwoMonthComparisonChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            Data: PropTypes.shape({
                Quotes: PropTypes.number,
                NewBusiness: PropTypes.number,
                Conversion: PropTypes.number,
                BoundPremium: PropTypes.number,
            }),
        })
    ),
};